import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Clock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, isToday } from 'date-fns';
import { supabase } from '../lib/supabase';

export default function Schedule({ onNavigate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    supabase.from('videos')
      .select('*, tiktok_accounts(username)')
      .then(({ data }) => setVideos((data ?? []).map(({ tiktok_accounts, ...v }) => ({
        ...v, account_name: tiktok_accounts?.username ?? null,
      }))));
  }, []);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const startDow = monthStart.getDay();
  const leadingBlanks = startDow === 0 ? 6 : startDow - 1;

  const scheduledVideos = videos.filter(v => v.scheduled_at);

  const videosOnDay = (day) =>
    scheduledVideos.filter(v => isSameDay(new Date(v.scheduled_at), day));

  const selectedVideos = videosOnDay(selectedDay);

  const monthScheduled = scheduledVideos.filter(v => isSameMonth(new Date(v.scheduled_at), currentMonth)).length;
  const monthPublished = videos.filter(v => v.status === 'published' && v.published_at && isSameMonth(new Date(v.published_at), currentMonth)).length;
  const monthFailed = videos.filter(v => v.status === 'failed' && v.created_at && isSameMonth(new Date(v.created_at), currentMonth)).length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Content Calendar</h1>
          <p style={styles.subtitle}>Track what's going live and when</p>
        </div>
        <button style={styles.addBtn} onClick={() => onNavigate('upload')}>
          + Add Video
        </button>
      </div>

      <div style={styles.layout}>
        <div style={styles.calCard}>
          <div style={styles.calHeader}>
            <button style={styles.navBtn} onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <ChevronLeft size={18} />
            </button>
            <h2 style={styles.monthTitle}>{format(currentMonth, 'MMMM yyyy')}</h2>
            <button style={styles.navBtn} onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <ChevronRight size={18} />
            </button>
          </div>

          <div style={styles.weekdays}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} style={styles.weekday}>{d}</div>
            ))}
          </div>

          <div style={styles.daysGrid}>
            {Array(leadingBlanks).fill(null).map((_, i) => (
              <div key={`blank-${i}`} style={styles.blankCell} />
            ))}
            {days.map(day => {
              const vids = videosOnDay(day);
              const selected = isSameDay(day, selectedDay);
              const today = isToday(day);
              const inMonth = isSameMonth(day, currentMonth);
              return (
                <button
                  key={day.toISOString()}
                  style={{
                    ...styles.dayCell,
                    background: selected ? 'var(--accent)' : today ? 'var(--accent-light)' : 'transparent',
                    color: selected ? '#fff' : inMonth ? 'var(--text-primary)' : 'var(--text-muted)',
                    borderColor: today && !selected ? 'var(--accent)' : 'transparent',
                  }}
                  onClick={() => setSelectedDay(day)}
                  onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                  onMouseLeave={e => { if (!selected) e.currentTarget.style.background = today ? 'var(--accent-light)' : 'transparent'; }}
                >
                  <span style={styles.dayNum}>{format(day, 'd')}</span>
                  {vids.length > 0 && (
                    <div style={styles.dayDots}>
                      {vids.slice(0, 3).map((_, i) => (
                        <span key={i} style={{ ...styles.dot, background: selected ? 'rgba(255,255,255,0.8)' : 'var(--accent)' }} />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={styles.detailCard}>
          <h3 style={styles.detailTitle}>{format(selectedDay, "MMMM d, yyyy")}</h3>
          <p style={styles.detailSub}>
            {selectedVideos.length === 0
              ? 'No videos scheduled for this day'
              : `${selectedVideos.length} video${selectedVideos.length > 1 ? 's' : ''} scheduled`}
          </p>

          <div style={styles.detailList}>
            {selectedVideos.length === 0 ? (
              <div style={styles.emptyDay}>
                <div style={styles.emptyIcon}>📅</div>
                <p style={styles.emptyText}>This day is free</p>
                <button style={styles.emptyAddBtn} onClick={() => onNavigate('upload')}>
                  Add Video
                </button>
              </div>
            ) : (
              selectedVideos.map(video => (
                <div key={video.id} style={styles.detailVideo}>
                  <div style={styles.detailThumb}>
                    <Play size={16} color="var(--text-muted)" />
                  </div>
                  <div style={styles.detailVideoInfo}>
                    <p style={styles.detailVideoTitle}>{video.title}</p>
                    <p style={styles.detailVideoMeta}>{video.account_name ? `@${video.account_name}` : '—'}</p>
                  </div>
                  <div style={styles.detailVideoTime}>
                    <Clock size={12} />
                    {format(new Date(video.scheduled_at), 'HH:mm')}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={styles.monthOverview}>
            <h4 style={styles.overviewTitle}>This Month</h4>
            <div style={styles.overviewStats}>
              <div style={styles.overviewStat}>
                <span style={styles.overviewNum}>{monthScheduled}</span>
                <span style={styles.overviewLabel}>Scheduled</span>
              </div>
              <div style={styles.overviewStat}>
                <span style={styles.overviewNum}>{monthPublished}</span>
                <span style={styles.overviewLabel}>Published</span>
              </div>
              <div style={styles.overviewStat}>
                <span style={styles.overviewNum}>{monthFailed}</span>
                <span style={styles.overviewLabel}>Failed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { flex: 1, padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 },
  subtitle: { fontSize: 14, color: 'var(--text-secondary)' },
  addBtn: {
    padding: '10px 20px', background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  layout: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' },
  calCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px' },
  calHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  navBtn: {
    width: 34, height: 34, borderRadius: 8, border: 'none', background: 'transparent',
    cursor: 'pointer', color: 'var(--text-secondary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  monthTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' },
  weekdays: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 },
  weekday: { textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', padding: '6px 0', letterSpacing: '0.5px' },
  daysGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
  blankCell: { height: 52 },
  dayCell: {
    height: 52, borderRadius: 10, border: '1px solid', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 3, transition: 'all 0.15s', padding: 0,
  },
  dayNum: { fontSize: 14, fontWeight: 600 },
  dayDots: { display: 'flex', gap: 3 },
  dot: { width: 4, height: 4, borderRadius: '50%' },
  detailCard: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: '24px',
    display: 'flex', flexDirection: 'column', gap: 16,
  },
  detailTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' },
  detailSub: { fontSize: 13, color: 'var(--text-secondary)', marginTop: -10 },
  detailList: { display: 'flex', flexDirection: 'column', gap: 8, minHeight: 80 },
  emptyDay: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '20px 0' },
  emptyIcon: { fontSize: 28 },
  emptyText: { fontSize: 13, color: 'var(--text-muted)' },
  emptyAddBtn: {
    padding: '7px 18px', background: 'var(--accent-light)', color: 'var(--accent)',
    border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  detailVideo: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px',
    background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)',
  },
  detailThumb: {
    width: 40, height: 40, borderRadius: 8, background: 'var(--bg-card)',
    border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  detailVideoInfo: { flex: 1, minWidth: 0 },
  detailVideoTitle: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  detailVideoMeta: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  detailVideoTime: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--accent)', whiteSpace: 'nowrap' },
  monthOverview: { borderTop: '1px solid var(--border)', paddingTop: 16 },
  overviewTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.8px' },
  overviewStats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  overviewStat: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 8px',
    background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)',
  },
  overviewNum: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' },
  overviewLabel: { fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' },
};
